import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { globalLayout } from '@jira/platform__layouts-global-layout/src/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { issueNavigatorGlobalRoute } from '@jira/router-routes__issue-navigator-routes/src/issueNavigatorGlobalRoute.tsx';
import { GlobalIssueNavigatorSkeleton } from '@jira/platform__skeletons/src/ui/issue-navigator/components/global-app.tsx';
import { NIN_NEW_ISSUE_NAVIGATOR } from '@jira/platform__observability-capabilities/src/constants.tsx';
import type { Route } from '@atlassian/react-resource-router';
import { getCommonGlobalNavigation } from './common/getCommonGlobalNavigation.tsx';
import { getCommonGlobalResources } from './common/getCommonGlobalResources.tsx';
import { getSsrCircuitBreaker } from './common/getSsrCircuitBreaker.tsx';
import { adjustSsrFetchTimeout } from './common/getSsrFetchTimeout.tsx';
import { getRouteReporting } from './common/getRouteReporting.tsx';
import {
	globalIssueNavigatorPageEntryPoint,
	globalIssueNavigatorPageEntryPointWithoutPreviewPanel,
} from './ui/index.tsx';

/**
 * Global Issue Navigator Route Entry
 * Handles the older `/issues` route scheme
 */
export const issueNavigatorGlobalRouteEntry: Route = createEntry(issueNavigatorGlobalRoute, () => ({
	group: 'issue-navigator',
	// Ignore `jql` in apdex match so JQL-only URL changes don't reset page-load timing.
	// Use `undefined` (not `[]`) on the FG-OFF path so the field stays absent — matches
	// pre-PR behavior exactly, in case any consumer distinguishes absent from empty.
	apdexIgnoreParams: fg('nin_jql_route_should_reload_mitigation') ? ['jql'] : undefined,
	meta: {
		capability: NIN_NEW_ISSUE_NAVIGATOR,
		reporting: getRouteReporting(),
	},
	skeleton: GlobalIssueNavigatorSkeleton,
	ufoName: 'global-issue-navigator-jfe',
	navigation: getCommonGlobalNavigation(),
	resources: [...getCommonGlobalResources()],
	layout: globalLayout,
	isHydrateRootEnabled: true,
	bifrost: {
		bifrostGroup: 'issue-navigator',
		manifestName: 'jira-spa-issue-navigator.json',
		// Experiment to test the impact of increasing the resource timeout for issue navigator routes with more performance SSR
		fetchTimeout: adjustSsrFetchTimeout(expVal('nin_ssr_resource_timeout', 'timeout', 0)),
		circuitBreakerConfig: getSsrCircuitBreaker(),
	},
	entryPoint: () =>
		fg('nin_glance_panel_technical_changes')
			? globalIssueNavigatorPageEntryPoint
			: globalIssueNavigatorPageEntryPointWithoutPreviewPanel,
	supportsPreviewPanel: true,
	excludeFromWrmDeferredLoading: true,
	// EXPERIMENTAL__shouldReload override: stage-1 mitigation for the upcoming `'jql?'`
	// addition to this route's `query` whitelist (delivered in EM-12530 / PR B). Once the
	// whitelist change lands, `match.query` will include `jql`, which causes
	// `isSameRouteMatch` to return `false` on JQL-only URL changes.
	//
	// At time of writing there are exactly two router plugins in AFM:
	//   - resources-plugin
	//   - entry-points-plugin
	//
	// resources-plugin: defaultShouldReload = `pluginId === 'resources-plugin' || !sameRoute`
	//   The first operand short-circuits to true → defaultShouldReload is invariant to
	//   whitelist changes. Resources always say "reload"; downstream getKey() determines
	//   whether each resource actually refetches.
	//
	// entry-points-plugin: defaultShouldReload = `false || !sameRoute = !sameRoute`
	//   Fully sensitive to sameRoute. Without this override, JQL-only URL changes would
	//   trigger fresh loadEntryPoint() calls — wasted Relay queries + suspense flashes.
	//
	// We allow-list both plugins explicitly (rather than catching all) so any future
	// plugin additions get default behavior unless explicitly opted in.
	EXPERIMENTAL__shouldReload: ({ context, prevContext, pluginId, defaultShouldReload }) => {
		if (!fg('nin_jql_route_should_reload_mitigation')) return defaultShouldReload;
		if (pluginId === 'entry-points-plugin' || pluginId === 'resources-plugin') {
			// If we're using IssueAppWithConditionalEntrypoint, we need to make
			// sure the selected issue's data is fetched
			if (
				prevContext.query.selectedIssue !== context.query.selectedIssue &&
				!expVal('nin_glance_panel', 'isEnabled', false) &&
				fg('nin_issue_app_conditional_entrypoint')
			) {
				return true;
			}

			// Reload-significant params for this route: `filter` only. If a new param is added
			// to the route's query whitelist that should also trigger reloads (e.g. orderBy,
			// viewMode), add it to this comparison — otherwise it will be silently ignored.
			return (
				prevContext.match.url !== context.match.url ||
				prevContext.query.filter !== context.query.filter
			);
		}
		return defaultShouldReload;
	},
}));
